/**
 * Date utility functions for event deadline management
 */

import { EventOption } from '../types';

const parseIsoDate = (value?: string): Date | null => {
  if (!value) return null;
  const normalized = value.trim().replace(/\//g, '-');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const date = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateRange = (start: Date, end?: Date | null): string => {
  const pad = (num: number) => String(num).padStart(2, '0');
  const startMonth = pad(start.getMonth() + 1);
  const startDay = pad(start.getDate());

  if (!end || start.toDateString() === end.toDateString()) {
    return `${startMonth}月${startDay}日`;
  }

  const endMonth = pad(end.getMonth() + 1);
  const endDay = pad(end.getDate());

  if (startMonth === endMonth) {
    return `${startMonth}月${startDay}-${endDay}日`;
  }

  return `${startMonth}月${startDay}日-${endMonth}月${endDay}日`;
};

export const normalizeEventDateRange = (event: EventOption): EventOption => {
  if (event.dateRange && event.dateRange.includes('月')) {
    return event;
  }

  const startFromFields = parseIsoDate(event.startDate);
  const endFromFields = parseIsoDate(event.endDate);

  if (startFromFields) {
    return {
      ...event,
      dateRange: formatDateRange(startFromFields, endFromFields)
    };
  }

  const raw = (event.dateRange || '').trim();
  const rangeMatch = raw.match(/^(\d{4}[-/]\d{2}[-/]\d{2})\s*(?:~|至|到|－|–|—|\s-\s)\s*(\d{4}[-/]\d{2}[-/]\d{2})$/);
  if (rangeMatch) {
    const start = parseIsoDate(rangeMatch[1]);
    const end = parseIsoDate(rangeMatch[2]);
    if (start) {
      return {
        ...event,
        dateRange: formatDateRange(start, end)
      };
    }
  }

  const singleDate = parseIsoDate(raw);
  if (singleDate) {
    return {
      ...event,
      dateRange: formatDateRange(singleDate)
    };
  }

  return event;
};

/**
 * Check if an event's registration deadline has passed
 * @param event - The event to check
 * @returns true if the deadline has passed, false otherwise
 */
export const isDeadlinePassed = (event: EventOption): boolean => {
  if (!event.deadlineDate) {
    // If no deadline date is set, registration is always open
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  const deadlineDate = new Date(event.deadlineDate);
  deadlineDate.setHours(23, 59, 59, 999); // End of deadline day

  return today > deadlineDate;
};

/**
 * Get days remaining until deadline
 * @param event - The event to check
 * @returns number of days remaining (negative if passed), or null if no deadline set
 */
export const getDaysUntilDeadline = (event: EventOption): number | null => {
  if (!event.deadlineDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const deadlineDate = new Date(event.deadlineDate);
  deadlineDate.setHours(0, 0, 0, 0);

  const diffTime = deadlineDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Check if deadline is approaching (within specified days)
 * @param event - The event to check
 * @param days - Number of days to consider as "approaching" (default: 7)
 * @returns true if deadline is within the specified days
 */
export const isDeadlineApproaching = (event: EventOption, days: number = 7): boolean => {
  const daysRemaining = getDaysUntilDeadline(event);

  if (daysRemaining === null) {
    return false;
  }

  return daysRemaining > 0 && daysRemaining <= days;
};

/**
 * Format deadline status for display
 * @param event - The event to check
 * @returns Status message for display
 */
export const getDeadlineStatus = (event: EventOption): {
  message: string;
  type: 'expired' | 'urgent' | 'normal' | 'none';
} => {
  const daysRemaining = getDaysUntilDeadline(event);

  if (daysRemaining === null) {
    return {
      message: '持續開放報名',
      type: 'none'
    };
  }

  if (daysRemaining < 0) {
    return {
      message: '報名已截止',
      type: 'expired'
    };
  }

  if (daysRemaining === 0) {
    return {
      message: '今日截止',
      type: 'urgent'
    };
  }

  if (daysRemaining <= 3) {
    return {
      message: `剩 ${daysRemaining} 天`,
      type: 'urgent'
    };
  }

  if (daysRemaining <= 7) {
    return {
      message: `剩 ${daysRemaining} 天`,
      type: 'normal'
    };
  }

  return {
    message: `剩 ${daysRemaining} 天`,
    type: 'normal'
  };
};

/**
 * Parse event date range and get end date
 * @param event - The event to parse
 * @returns End date of the event or null if cannot parse
 */
export const getEventEndDate = (event: EventOption): Date | null => {
  try {
    // dateRange format: "MM月DD-DD日" or "MM月DD日"
    const match = event.dateRange.match(/(\d{1,2})月(\d{1,2})-?(\d{1,2})?日/);

    if (!match) {
      return null;
    }

    const month = parseInt(match[1], 10);
    const endDay = match[3] ? parseInt(match[3], 10) : parseInt(match[2], 10);

    const endDate = new Date(event.year, month - 1, endDay);
    endDate.setHours(23, 59, 59, 999); // End of day

    return endDate;
  } catch {
    return null;
  }
};

/**
 * Check if an event has ended (event date has passed)
 * @param event - The event to check
 * @returns true if the event has ended, false otherwise
 */
export const isEventEnded = (event: EventOption): boolean => {
  const endDate = getEventEndDate(event);

  if (!endDate) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today > endDate;
};

/**
 * Event registration status types
 */
export type EventRegistrationStatus = 'accepting' | 'upcoming' | 'ended' | 'full';

/**
 * Get the current registration status of an event
 * @param event - The event to check
 * @returns
 *   - 'accepting': Registration is still open (deadline not passed)
 *   - 'upcoming': Registration closed but event hasn't started yet
 *   - 'ended': Event has finished
 */
export const getEventRegistrationStatus = (event: EventOption): EventRegistrationStatus => {
  const eventEnded = isEventEnded(event);
  if (eventEnded) {
    return 'ended';
  }

  const deadlinePassed = isDeadlinePassed(event);
  if (deadlinePassed) {
    return 'upcoming';
  }

  if (event.registrationClosed) {
    return 'full';
  }

  return 'accepting';
};

/**
 * Check if registration is currently open for an event
 * @param event - The event to check
 * @returns true if registration is open (not ended, not past deadline, not closed)
 */
export const isRegistrationOpen = (event: EventOption): boolean => {
  return getEventRegistrationStatus(event) === 'accepting';
};


/**
 * 取得梯隊開始日：優先使用 startDate，其次解析 dateRange（MM月DD日 / MM/DD）。
 */
export const getEventStartDate = (event: EventOption): Date | null => {
  const fromField = parseIsoDate(event.startDate);
  if (fromField) return fromField;
  const raw = event.dateRange || '';
  const m = raw.match(/(\d{1,2})月(\d{1,2})/) || raw.match(/(\d{1,2})\/(\d{1,2})/);
  if (!m || !event.year) return null;
  const date = new Date(event.year, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** 往前推 N 個月；遇到月底（如 3/31 → 2 月）自動取該月最後一天。 */
export const shiftMonths = (date: Date, months: number): Date => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDay));
  return target;
};

export const toIsoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const toMonthDayLabel = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}月${pad(date.getDate())}日`;
};

/** 預設截止日：出隊前一個月。 */
export const getDefaultCutoffFromStart = (start: Date): Date => shiftMonths(start, -1);

/**
 * 線上取消截止日：管理員有設定就用設定值；否則預設為出隊前一個月。
 */
export const getCancelDeadlineDate = (event: EventOption): Date | null => {
  const custom = parseIsoDate(event.cancelDeadlineDate);
  if (custom) return custom;
  const start = getEventStartDate(event);
  return start ? getDefaultCutoffFromStart(start) : null;
};

/** 是否已超過線上取消期限（截止日當天仍可取消）。 */
export const isCancelDeadlinePassed = (event: EventOption): boolean => {
  const cutoff = getCancelDeadlineDate(event);
  if (!cutoff) return false;
  const end = new Date(cutoff);
  end.setHours(23, 59, 59, 999);
  return new Date() > end;
};
